import React, { Dispatch, ReactElement, SetStateAction } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";

import { Template } from "../../services/api";
import { TemplateSetAggregate } from "../../services/api/TemplateSets";

const TemplateForm: React.FC<{
  actionButtons?: ReactElement;
  template: Template;
  setTemplate: Dispatch<SetStateAction<Template>>;
  templateSets: TemplateSetAggregate[];
}> = ({ actionButtons, template, setTemplate, templateSets }) => {
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    // Since event.target.value always string.
    const expectedType = template.getType(name);
    const newValue = expectedType === "number" ? Number(value) : value;
    setTemplate((prevTemplate) => {
      return new Template({ ...prevTemplate!, [name]: newValue });
    });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTemplate((prevTemplate) => {
      const newSetIds = new Set(prevTemplate.set_ids);
      if (event.target.checked) {
        newSetIds.add(event.target.name);
      } else {
        newSetIds.delete(event.target.name);
      }

      return new Template({
        ...prevTemplate!,
        set_ids: Array.from(newSetIds),
      });
    });
  };

  const renderMappedSets = () => {
    const err = template.hasError("set_ids");

    return (
      <FormControl sx={{ m: 3 }} component="fieldset" variant="standard" required error={!!err}>
        <FormLabel component="legend">mapped sets</FormLabel>
        <FormGroup>
          {templateSets.map(({ id, name, disabled }) => (
            <FormControlLabel
              key={`set-${id}`}
              control={
                <Checkbox
                  checked={!!template.set_ids.find((v) => v === id)}
                  name={id}
                  onChange={handleCheckboxChange}
                />
              }
              label={disabled ? `${id} - ${name} (deleted)` : `${id} - ${name}`}
            />
          ))}
        </FormGroup>
        <FormHelperText data-testid="error-text-mapped_sets" error={!!err}>
          {err ?? null}
        </FormHelperText>
      </FormControl>
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid item md={4} xs={12}>
        <Typography sx={{ fontSize: "20pt", wordBreak: "break-word" }}>{template.name}</Typography>
      </Grid>
      <Grid item md={true}>
        <Box
          sx={{
            "& > :not(style)": { m: 1, width: "100%" },
          }}
          component="form"
          autoComplete="off"
        >
          {template &&
            Object.entries(template.fieldsWithType()).map(([key, value]) => {
              if (["name", "set_ids"].indexOf(key) > -1) return null;

              const error = template.hasError(key);
              return (
                <TextField
                  required
                  name={key}
                  key={key}
                  type={value}
                  label={key.replace("_", " ")}
                  variant="standard"
                  autoComplete="off"
                  error={!!error}
                  helperText={error}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {error ? (
                          <ErrorIcon sx={{ color: "red" }} data-testid={`error-icon-${key}`} />
                        ) : (
                          <CheckCircleIcon sx={{ color: "green" }} data-testid={`valid-icon-${key}`} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                  value={template[key as keyof Template] || ""}
                  onChange={handleInputChange}
                />
              );
            })}
          {renderMappedSets()}
        </Box>
        {actionButtons}
      </Grid>
    </Grid>
  );
};

export default TemplateForm;
